<template>
  <!--
    审批操作面板：单据内容 + 审批意见 + 操作按钮。

    抽成独立组件是因为它有两个宿主：
    1 审批中心的弹框（从列表行进来，任务信息现成）
    2 企业微信消息链接打开的独立页面（只有 instanceId，任务要自己查）
    两处必须看到同一份单据、有同一套操作，复制一份迟早会改漏一边。
  -->
  <div class="approval-action-panel" :class="{mobile: mobile}">
    <!-- ==================== 移动端（企业微信手机端） ==================== -->
    <!--
      与浏览器端同一份逻辑、同一套接口，只是换成移动端的卡片 + 底栏布局。
      做成一个组件里的两个分支而不是两个组件：任务取数、审批动作、意见回显都一样，
      拆成两份迟早会「电脑上能批、手机上点不动」。
    -->
    <template v-if="mobile">
      <list-card v-if="task" :label="task.bizTitle">
        <template #desc>
          <span class="mobile-task-tag" :class="task.taskTypeName === '抄送' ? 'tag-info' : 'tag-warning'">{{ task.taskTypeName || "审批" }}</span>
        </template>
        <data-column label="业务类型">{{ task.businessName || task.businessType || "—" }}</data-column>
        <data-column label="当前节点">{{ task.nodeName || "—" }}</data-column>
        <data-column label="发起人">{{ task.startUserName || "—" }}</data-column>
        <data-column label="发起时间">{{ fmtTime(task.startTime) }}</data-column>
      </list-card>

      <!-- 单据内容：审批人要看着单据才能做判断 -->
      <list-card label="单据内容" type="form">
        <div v-loading="detailLoading">
          <approval-form :fields="businessFields" :empty-text="detailEmptyText" :columns="1"/>
        </div>
      </list-card>

      <!-- 抄送只是通知，确认不需要填意见，省掉这个输入框 -->
      <!-- 意见是要写一段话的，用整块 textarea 而不是抽屉输入：一眼能看出能写多行 -->
      <list-card v-if="isDecideMode || isConfirmMode" label="审批意见" type="form">
        <el-input class="opinion-textarea" v-model="approveForm.opinion" type="textarea" :rows="3"
                  placeholder="选填；驳回时请说明原因，发起人据此修改"/>
      </list-card>

      <div v-if="isConfirmMode" class="mobile-tip">
        确认后该抄送将从你的待办中移除。<b>确认与否不影响流程流转</b>，此单据的审批会照常进行。
      </div>
      <div v-if="isViewMode" class="mobile-tip">
        <template v-if="viewOpinion">我的处理意见：<b>{{ viewOpinion }}</b></template>
        <template v-else>该任务已处理，此处仅供查看。</template>
      </div>
    </template>

    <!-- ==================== 浏览器端（原样） ==================== -->
    <template v-else>
    <div v-if="task" class="approve-summary">
      <div class="summary-title">{{ task.bizTitle }}</div>
      <div class="summary-meta">
        <span>{{ task.businessName || task.businessType }}</span>
        <span>当前节点：{{ task.nodeName || '—' }}</span>
        <span>发起人：{{ task.startUserName || '—' }}</span>
      </div>
    </div>

    <!-- 单据内容：审批人要看着单据才能做判断 -->
    <div class="section-title dialog-section">单据内容</div>
    <div v-loading="detailLoading" class="detail-wrap">
      <approval-form :fields="businessFields" :empty-text="detailEmptyText" :columns="formColumns"/>
    </div>

    <!-- 抄送只是通知，确认不需要填意见，省掉这个输入框 -->
    <form-items v-if="isDecideMode || isConfirmMode" :data="approveForm" :columns="1" label-width="2.4rem">
      <form-item label="审批意见" :required="false" name="opinion">
        <el-input v-model="approveForm.opinion" type="textarea" :rows="3"
                  placeholder="选填；驳回时请说明原因，发起人据此修改"/>
      </form-item>
    </form-items>

    <div v-if="isConfirmMode" class="confirm-tip">
      确认后该抄送将从你的待办中移除。<b>确认与否不影响流程流转</b>，此单据的审批会照常进行。
    </div>
    <!-- 已处理的任务：把当时填的意见回显出来，这是「查看」最该看到的东西 -->
    <div v-if="isViewMode" class="confirm-tip">
      <template v-if="viewOpinion">
        我的处理意见：<b>{{ viewOpinion }}</b>
      </template>
      <template v-else>
        该任务已处理，此处仅供查看。
      </template>
    </div>

    </template>

    <!-- 移动端：固定底栏（页面给 body 留出 80px 的内边距） -->
    <div v-if="mobile" class="mobile-action-bar">
      <template v-if="isViewMode">
        <mobile-button el-type="primary" @click="$emit('close')">关闭</mobile-button>
      </template>
      <template v-else-if="isDecideMode">
        <!-- 四个按钮：取消/中止收成图标，驳回与同意拼成双色按钮 -->
        <mobile-button-group>
          <mobile-button icon="element-ui_close" @click="$emit('close')">取消</mobile-button>
          <mobile-button icon="element-ui_circle-close" el-type="danger" @click="abortTask">中止</mobile-button>
          <mobile-button icon="element-ui_back" el-type="warning" @click="submitApprove(APPROVE.REJECT)">驳回</mobile-button>
          <mobile-button icon="element-ui_check" @click="submitApprove(APPROVE.PASS)">同意</mobile-button>
        </mobile-button-group>
      </template>
      <template v-else>
        <mobile-button @click="$emit('close')">取消</mobile-button>
        <mobile-button el-type="primary" @click="submitApprove()">{{ title }}</mobile-button>
      </template>
    </div>

    <!-- 浏览器端：跟随内容的操作栏 -->
    <div v-if="!mobile" class="action-bar">
      <!-- 查看：只给关闭 -->
      <template v-if="isViewMode">
        <page-button el-type="primary" @click="$emit('close')">关闭</page-button>
      </template>
      <!-- 审批：看完单据后在这里一次性决定结果，不预设同意或驳回 -->
      <template v-else-if="isDecideMode">
        <page-button @click="$emit('close')">取消</page-button>
        <!-- 中止与驳回并列：驳回是打回让发起人改了再交，中止是彻底停下不再流转 -->
        <page-button type="write" el-type="info" @click="abortTask">中止</page-button>
        <page-button type="write" el-type="danger" @click="submitApprove(APPROVE.REJECT)">驳回</page-button>
        <page-button type="write" el-type="success" @click="submitApprove(APPROVE.PASS)">同意</page-button>
      </template>
      <!-- 抄送确认 -->
      <template v-else>
        <page-button @click="$emit('close')">取消</page-button>
        <page-button type="write" el-type="primary" @click="submitApprove()">{{ title }}</page-button>
      </template>
    </div>
  </div>
</template>

<script>
import ApprovalForm from "./ApprovalForm.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import MobileButtonGroup from "@/components/qywx/button/MobileButtonGroup.vue";
import checkApi from "@/api/dz-project/project/checkApi";

/**
 * 审批动作与操作模式，与审批中心同一套口径。
 *
 * CONFIRM 复用同一份单据加载逻辑 —— 抄送人同样要看着单据才能确认，
 * 不该因为「只是通知」就让他对着一个没有内容的框点确定。
 */
export const APPROVE = {
  PASS: 1,     // 同意
  REJECT: 2,   // 驳回
  CONFIRM: 3,  // 抄送确认
  VIEW: 4,     // 查看已处理的任务
  DECIDE: 5,   // 审批：看完单据再决定通过还是驳回
};

export default {
  name: "ApprovalActionPanel",
  components: {ApprovalForm, FormItems, FormItem, PageButton, ListCard, DataColumn, MobileButton, MobileButtonGroup},
  props: {
    /** 当前任务（待办列表行或按 instanceId 查回来的任务） */
    task: {type: Object, default: null},
    /** 移动端布局：卡片 + 固定底栏。逻辑与浏览器端完全一致，只是排版不同 */
    mobile: {type: Boolean, default: false},
    /** 操作模式，取值见 APPROVE */
    mode: {type: Number, default: APPROVE.PASS},
    /** 单据字段的排列列数，窄屏（手机）传 1 */
    formColumns: {type: Number, default: 2},
  },
  emits: ["close", "refresh"],
  data() {
    return {
      APPROVE,
      approveForm: {opinion: ""},
      businessFields: [],
      detailLoading: false,
      detailEmptyText: "该业务未配置单据详情展示",
    };
  },
  computed: {
    title() {
      if (this.mode === APPROVE.VIEW) {
        return "查看单据";
      }
      if (this.mode === APPROVE.CONFIRM) {
        return "确认抄送";
      }
      // 审批模式下标题不定结果 —— 审批人还没看单据，此时说「同意/驳回」都不合适
      return "审批单据";
    },
    isConfirmMode() {
      return this.mode === APPROVE.CONFIRM;
    },
    /** 查看已处理的任务：除关闭外不提供任何操作 */
    isViewMode() {
      return this.mode === APPROVE.VIEW;
    },
    /** 审批模式：底部给「同意 / 驳回」两个按钮，由审批人看完单据后选 */
    isDecideMode() {
      return this.mode === APPROVE.DECIDE;
    },
    /**
     * 查看模式回显我自己当时的处理意见。
     *
     * 取 opinion 而不是拼「同意/驳回 + 意见」：后端写入时已经把动作放进去了
     * （如「同意：请补充附件」），再拼一次会变成「同意：同意：请补充附件」。
     */
    viewOpinion() {
      return (this.task && this.task.opinion) || "";
    },
  },
  watch: {
    /** 换任务时要重新拉单据，并把上一条的意见清掉，避免把上一单的意见带到这一单 */
    task: {
      immediate: true,
      handler(val) {
        this.approveForm.opinion = "";
        this.businessFields = [];
        this.detailEmptyText = "该业务未配置单据详情展示";
        if (val && val.instanceId) {
          this.loadBusinessDetail(val.instanceId);
        }
      },
    },
  },
  methods: {
    /**
     * 加载单据内容。
     *
     * 失败不阻断审批：取不到详情时给出明确提示，审批人仍可选择继续批或先驳回。
     * 静默失败会让审批人对着空表单做判断，那才是真正的危险。
     */
    async loadBusinessDetail(instanceId) {
      this.detailLoading = true;
      try {
        const list = await checkApi.queryBusinessDetail(instanceId);
        this.businessFields = list || [];
        if (!this.businessFields.length) {
          this.detailEmptyText = "该业务未配置单据详情展示";
        }
      } catch (e) {
        this.businessFields = [];
        this.detailEmptyText = "单据内容加载失败，请谨慎审批";
        this.$message.warning("单据内容加载失败，请确认无误后再审批");
      } finally {
        this.detailLoading = false;
      }
    },
    /**
     * 中止流程。
     *
     * 与驳回并列的终局动作，所以单独一个按钮 + 二次确认 ——
     * 中止后单据不再流转，误点代价比驳回大得多。
     */
    async abortTask() {
      if (!this.task) {
        return;
      }
      try {
        await this.$confirm("中止后该单据将不再流转，审批流程彻底结束。确认中止？", "中止流程",
            {type: "warning", confirmButtonText: "确认中止", cancelButtonText: "取消"});
      } catch (e) {
        return; // 用户取消
      }
      await checkApi.abort({taskId: this.task.id, opinion: this.approveForm.opinion.trim()});
      this.$message.success("已中止");
      this.$emit("close");
      this.$emit("refresh");
    },
    /**
     * 提交审批。
     *
     * @param result 审批结果。仅「审批」模式需要传 ——
     *               那种模式下结果是在看完单据后才选的，不是打开前就定好的。
     *               抄送确认不传（它没有同意/驳回之分）。
     */
    fmtTime(value) {
      if (!value) {
        return "—";
      }
      return String(value).replace("T", " ").slice(0, 16);
    },
    async submitApprove(result) {
      if (!this.task) {
        return;
      }
      // 驳回必须说明原因：不说清楚，发起人不知道该改什么，只能反复提交
      if (result === APPROVE.REJECT && !this.approveForm.opinion.trim()) {
        this.$message.error("驳回必须填写意见");
        return;
      }
      const payload = {taskId: this.task.id, opinion: this.approveForm.opinion.trim()};
      if (this.isConfirmMode) {
        // 抄送确认走独立接口：它不推进流程、不发回调，
        // 只是把这条通知从待办里摘掉
        await checkApi.confirmCopy({taskId: this.task.id});
      } else if (result === APPROVE.PASS) {
        await checkApi.pass(payload);
      } else if (result === APPROVE.REJECT) {
        await checkApi.reject(payload);
      } else {
        // 走到这里说明调用方忘了传结果，属于代码错误，不该静默提交
        this.$message.error("请选择审批结果");
        return;
      }
      this.$message.success(result === APPROVE.REJECT ? "已驳回" : "已同意");
      this.$emit("close");
      this.$emit("refresh");
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-action-panel {
  // ---------------- 移动端（企业微信手机端） ----------------
  &.mobile{
    // 标题折两行：审批事项通常很长，右上角还有个任务类型标签
    :deep(.card-title){
      height: auto;
      min-height: 60px;
      .name{
        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        line-height: 1.3;
      }
      .desc{
        display: flex;
        align-items: center;
        gap: 6px;
      }
    }
    // 任务类型标签
    .mobile-task-tag{
      flex: none;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: var(--yq-font-size-mini-mobile);
      &.tag-info{
        color: var(--el-color-info);
        background: var(--el-fill-color-light);
      }
      &.tag-warning{
        color: var(--el-color-warning);
        background: var(--el-color-warning-light-9);
      }
    }
    // 审批意见：整块 textarea，去掉自带边框，用浅灰圆角块
    :deep(.opinion-textarea){
      .el-textarea__inner{
        box-shadow: none;
        background: var(--yq-background-color);
        border-radius: 6px;
        padding: 10px;
        font-size: var(--yq-font-size-mini-mobile);
        line-height: 1.6;
      }
    }
    // 抄送 / 已处理的说明条
    .mobile-tip{
      margin-top: 10px;
      padding: 10px 12px;
      border-radius: 6px;
      background: #FAFBFC;
      border: 1px solid var(--yq-border-color);
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
      line-height: 1.7;
    }
    // 固定底栏（宿主页面给 body 留出内边距，别被盖住）
    .mobile-action-bar{
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100;
      display: flex;
      gap: 10px;
      padding: 10px;
      background: #FFF;
      box-shadow: 0 -1px 4px var(--yq-shadow);
      :deep(.mobile-button){
        flex: 1;
      }
      :deep(.mobile-button-group){
        flex: 1;
        min-width: 0;
      }
    }
    // 单据内容：把审批表单的行改成移动端的「标签 + 值 + 分隔线」
    :deep(.approval-form .form-item){
      display: flex;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid var(--yq-border-color);
      &:last-child{
        border-bottom: none;
      }
      .form-label{
        color: var(--yq-font-color-desc);
      }
      .form-value{
        text-align: right;
      }
    }
  }

  .approve-summary {
    background: #fafbfc;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 12px;

    .summary-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 6px;
    }

    .summary-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      font-size: 12px;
      color: #909399;
    }
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin: 14px 0 10px;
  }

  .dialog-section {
    margin: 4px 0 10px;
  }

  .detail-wrap {
    min-height: 60px;
    margin-bottom: 4px;
  }

  .confirm-tip {
    font-size: 12px;
    line-height: 1.8;
    color: #909399;
    background: #fafbfc;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 10px 12px;

    b {
      color: #606266;
    }
  }

  // 操作栏自带，不放宿主容器的 footer：
  // 弹框与独立页面的容器不同，按钮跟着组件走才能两处一致
  .action-bar {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid var(--yq-border-color, #ebeef5);
  }
}
</style>
